import csvtojson from 'csvtojson';

// ==========================================
// AUXILIARY FUNCTIONS
// ==========================================

/**
 * Filter deleted questions and sort by ID
 */
function filterAndSortQuestions(questions) {
  return questions.sort((a, b) => {
    if (a.deleted !== b.deleted) {
      return a.deleted ? 1 : -1;
    }
    return (a.id || 0) - (b.id || 0);
  });
}

/**
 * Normalizes a question with a flat structure to a standard format for export
 * @param {Object} item - Question in flat structure (from CSV endpoint)
 * @returns {Object} - Normalized row for export
 */
function normalizeQuestionForExport(item) {
  return {
    questionID: item.questionID || item.id,
    status: item.status || '',
    sourceQuestion: item.sourceQuestion || '',
    chapter: item.chapter || '',
    deletedAt: item.deletedAt || '',
    AI_question: item.AI_question || '',
    AI_question_change: item.AI_question_change || '',
    AI_answer: item.AI_answer || '',
    AI_answer_change: item.AI_answer_change || '',
    question: item.question || item.text,
    questionImageLink: item.questionImageLink || '',
    correctanswer: item.correctanswer || '',
    correctanswerImageLink: item.correctanswerImageLink || '',
    incorrectanswer_0: item.incorrectanswer_0 || '',
    incorrectanswerImageLink_0: item.incorrectanswerImageLink_0 || '',
    incorrectanswer_1: item.incorrectanswer_1 || '',
    incorrectanswerImageLink_1: item.incorrectanswerImageLink_1 || '',
    incorrectanswer_2: item.incorrectanswer_2 || '',
    incorrectanswerImageLink_2: item.incorrectanswerImageLink_2 || '',
    notes: item.notes || '',
    softwarepage: item.softwarepage || item.softwarePage || '',
    flashmode: item.flashmode,
    flashmodeonly: item.flashmodeonly,
    documentpage: item.documentpage || '',
    deleted: item.deleted || '',
    link: item.link || '',
    AI_prompt: item.AI_prompt || '',
    aiAdminFeedback: stripFeedbackHtml(item.aiAdminFeedback),
    up_votes: item.up_votes || 0,
    down_votes: item.down_votes || 0,
  };
}

/**
 * Process an array of questions for export
 * @param {Array} questions - Array of questions in flat structure
 * @returns {Array} - Array of normalized and ordered rows
 */
export function processQuestionsForExport(questions) {
  if (!questions || !Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  const sortedQuestions = filterAndSortQuestions(questions);
  return sortedQuestions.map(normalizeQuestionForExport);
}

// ==========================================
// EXPORT FUNCTIONS
// ==========================================

/**
 * Export questions to CSV format
 */
export function exportToCsv(filename, rows) {
  const processRow = (row) => {
    let finalVal = '';
    for (let j = 0; j < row.length; j++) {
      let innerValue =
        typeof row[j] === 'undefined' || row[j] === null
          ? ''
          : row[j].toString();
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString();
      }
      let result = innerValue.replace(/\r?\n|\r/g, ' ');
      if (result.search(/("|,|\n)/g) >= 0) result = `"${result}"`;
      if (j > 0) finalVal += ',';
      finalVal += result;
    }
    return `${finalVal}\n`;
  };

  let csvFile = '';
  for (let i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i]);
  }
  const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

/**
 * Downloads questions in Excel format (XLSX or XLS)
 * @param {Array} questions - Array of questions in flat structure (from CSV endpoint)
 * Each question should have: questionID, correctanswer, incorrectanswer_0/1/2, etc.
 * @param {string} format - File format: 'xls' or 'xlsx'
 * @param {Object} chapter - Chapter object with name and id for filename generation
 */
export function downloadXLSX(questions, format, chapter = null) {
  const processedQuestions = processQuestionsForExport(questions);

  if (processedQuestions.length === 0) {
    return;
  }

  const ws = window.XLSX.utils.json_to_sheet(processedQuestions);
  const wb = window.XLSX.utils.book_new();

  const wscols = [
    { wch: 10 }, // questionID
    { wch: 30 }, // AI_question
    { wch: 20 }, // AI_question_change
    { wch: 30 }, // AI_answer
    { wch: 20 }, // AI_answer_change
    { wch: 50 }, // question
    { wch: 30 }, // correctanswer
    { wch: 30 }, // incorrectanswer_0
    { wch: 30 }, // incorrectanswer_1
    { wch: 30 }, // incorrectanswer_2
    { wch: 30 }, // notes
    { wch: 13 }, // softwarepage
    { wch: 13 }, // flashmode
    { wch: 13 }, // flashmodeonly
    { wch: 13 }, // documentpage
    { wch: 10 }, // deleted
    { wch: 40 }, // link
    { wch: 50 }, // AI_prompt
    { wch: 30 }, // aiAdminFeedback
    { wch: 10 }, // up_votes
    { wch: 10 }, // down_votes
  ];

  ws['!cols'] = wscols;
  window.XLSX.utils.book_append_sheet(wb, ws, 'Questions');

  const downloadfilename = generateDownloadFilename(chapter, format);
  window.XLSX.writeFile(wb, downloadfilename);
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

export function equalArray(a1, a2) {
  return a1.length === a2.length && a1.every((v, i) => v === a2[i]);
}

export function hasDownloadCSV(row) {
  return row.findIndex((data) => data === 'Books.DownloadCSV') !== -1;
}

export function allowRolesPermissions(rolesPermissions) {
  const permission = [];
  rolesPermissions.map((role) => {
    permission.push(hasDownloadCSV(role.actions));
    return role;
  });
  return permission.some((elem) => elem === true);
}

export function isCsvFile(file) {
  return (
    file?.type === 'text/csv' || file?.name?.toLowerCase().endsWith('.csv')
  );
}

/**
 * @async
 * @param {File} file - The CSV file to parse. Returns an empty array on an invalid file.
 * @param {string[]} [headers=[]] - Optional array of attribute/key names for the parsed objects.
 * @returns {Promise<Object[]>} A promise that resolves to an array of objects representing the CSV data.
 */
export async function parseCsvFile(file, headers = []) {
  if (!isCsvFile(file)) return [];

  const reader = new FileReader();
  const csvString = await new Promise((resolve, reject) => {
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });

  if (headers?.length > 0)
    return csvtojson({ noheader: false, headers }).fromString(csvString);
  return csvtojson({ noheaders: true }).fromString(csvString);
}

/**
 * Generate a consistent file name for downloads
 * @param {Object} chapter - Chapter object with name and id properties
 * @param {string} format - File extension ('csv', 'xlsx', 'xls')
 * @returns {string} Formatted filename with chapter name and date
 * @example
 * generateDownloadFilename({ name: 'TOPIC CHINA', id: 18737 }, 'xlsx')
 * Returns: "TOPIC CHINA-questions-Oct08-2025.xlsx"
 */
export function generateDownloadFilename(chapter, format) {
  if (!chapter || !chapter.name) {
    return `questions.${format}`;
  }

  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'short' }); // 'Oct'
  const day = String(now.getDate()).padStart(2, '0'); // '08'
  const year = now.getFullYear(); // '2025'

  return `${chapter.name}-questions-${month}${day}-${year}.${format}`;
}

/**
 * Strips HTML tags from aiAdminFeedback and preserves line breaks
 * as actual newlines for CSV/XLSX export.
 */
export function stripFeedbackHtml(value) {
  if (!value) return '';
  return value
    .replace(/<\/p>\s*<p>/g, '\n')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<\/?p>/g, '')
    .trim();
}
